import {
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GoogleMapsModule, MapInfoWindow } from '@angular/google-maps';
import { MarkerClusterer, Renderer } from '@googlemaps/markerclusterer';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { serverErrorMessage } from '../../helpers/api-error.util';
import { getStoredUser } from '../../helpers/user-storage.util';
import { AvailableWorkService } from '../../services/available-work.service';
import { CommonUtilityServiceService } from '../../services/common-utility-service.service';
import { GoogleMapsLoaderService } from '../../services/google-maps-loader.service';
import { UiSecretsService } from '../../services/ui-secrets.service';
import { AvailableWork } from '../../model/available-work';
import {
  availableWorkAddressSource,
  availableWorkFacetCounts,
  AvailableWorkFilter,
  availableWorkMetadataLine,
  availableWorkPosition,
  availableWorkPropertyKey,
  availableWorkProximity,
  availableWorkReportType,
  availableWorkReportTypeOptions,
  AvailableWorkRowView,
  AvailableWorkStatusFacet,
  buildWorkMapRows,
  filterAvailableWorkRows,
  filterProjectRows,
  hasEnabledServiceTypes,
  matchesAvailableWorkReportType,
  offersFacetCounts,
  OffersFacet,
  ProjectRowView,
  WorkMapStatus,
  projectDueDateCounts,
  projectReportTypeOptions,
  ProjectRowFilter,
  sortAvailableWorkRowsByProximity,
  unknownAvailableWorkProximity,
  workMapProjectRoute,
  workMapRowStatus,
  workMapRowWorkType,
  WorkMapDueDate,
  WorkMapRow,
  WorkMapRowView,
  WorkMapScope,
  WorkMapUrgency,
} from './work-map-scope.util';
import { buildTestId, createTestIdScope } from '../../shared/testid/testid.util';

// Continental-US default view when there is nothing to fit bounds to. Same
// values `CommonUtilityServiceService.determineMapCenter` falls back to.
const US_CENTER: google.maps.LatLngLiteral = { lat: 39.8283, lng: -98.5795 };
const US_DEFAULT_ZOOM = 4;
// Zoom applied when a single row is focused (row click) or a lone marker is fit.
const FOCUS_ZOOM = 14;

// Fill for the currently-selected marker — brand green (CREtelligent lime) so
// the clicked site stands out from the status-colored (grey / red) markers
// around it. Selection is a highlight accent, not a status.
const SELECTED_MARKER_COLOR = '#A1CD3A';

// Cluster bubbles encode DENSITY (marker count), NOT status: a monochrome blue
// ramp from light blue (low) → CREtelligent navy (high). Deliberately a single
// blue hue that matches nothing in the urgency/status legends, so a dense
// cluster is never misread as "many overdue/critical sites". Mirrors the User
// Location Map's cluster styling so the two maps read consistently.
const CLUSTER_LOW_COLOR = '#B5D4F4'; // light blue — low density
const CLUSTER_HIGH_COLOR = '#001F4C'; // CREtelligent navy — high density

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Linear RGB interpolation between two hex colors → an `rgb(...)` string. */
function lerpColor(from: string, to: string, t: number): string {
  const [fr, fg, fb] = hexToRgb(from);
  const [tr, tg, tb] = hexToRgb(to);
  const r = Math.round(fr + (tr - fr) * t);
  const g = Math.round(fg + (tg - fg) * t);
  const b = Math.round(fb + (tb - fb) * t);
  return `rgb(${r}, ${g}, ${b})`;
}

/** Readable label color for a bubble fill — dark navy on light fills, white on dark. */
function labelColorFor(rgb: string): string {
  const [r, g, b] = (rgb.match(/\d+/g) ?? ['0', '0', '0']).map(Number);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#001F4C' : '#ffffff';
}

/** Urgency → { legend label, marker/dot color }. Drives markers AND the legend. */
const URGENCY_META: Record<WorkMapUrgency, { label: string; color: string }> = {
  // Red / amber / green reuse the User Location Map's status palette so the two
  // maps read consistently; neutral grey is the no-due-date fallback.
  overdue: { label: 'Overdue', color: '#a32d2d' },
  soon: { label: 'Due within 7 days', color: '#8a5a00' },
  onTrack: { label: 'On track', color: '#3b6d11' },
  none: { label: 'No due date', color: '#6b7280' },
};

/**
 * My Offers status → { legend label, marker/dot color }. A rush-driven palette
 * (Critical red / non-critical grey); the non-critical bucket is labeled
 * "Standard". Drives the My Offers markers, row status label, and legend.
 */
const OFFERS_STATUS_META: Record<WorkMapStatus, { label: string; color: string }> = {
  needsStaffing: { label: 'Standard', color: URGENCY_META.none.color },
  critical: { label: 'Critical', color: URGENCY_META.overdue.color },
};

/**
 * Available Work status → { legend label, marker/dot color }. The SAME rush
 * palette as My Offers (both are opportunity scopes fed by the same `rush`
 * flag through `workMapRowStatus`), relabeled for this surface: the neutral
 * bucket reads "Needs staffing" here, not "Standard".
 */
const AVAILABLE_WORK_STATUS_META: Record<
  WorkMapStatus,
  { label: string; color: string }
> = {
  needsStaffing: { label: 'Needs staffing', color: URGENCY_META.none.color },
  critical: { label: 'Critical', color: URGENCY_META.overdue.color },
};

// Shown when a withdrawal answers `removed: false` — see `withdrawInterest`.
// Deliberately says only what the client can know: the four causes are
// indistinguishable, so naming any one of them ("no longer available", "already
// withdrawn") would assert one in four. The backend's own success copy must not
// be reused here — it says a withdrawal happened, which on this branch it did
// not. The second sentence is what explains the feed refreshing under them.
const WITHDRAWAL_UNCONFIRMED_MESSAGE =
  "We couldn't confirm that withdrawal. Refreshing your available work.";

/** Filter tab/option descriptor with a live count. */
interface FacetOption<T> {
  value: T;
  label: string;
  count: number;
}

/**
 * Dashboard work map (Spec 10, Task 3) — a Google map with urgency-colored
 * markers, a synced side list, a legend, and a no-API-key list-only fallback.
 * A three-scope toggle switches what the map plots:
 *   • My Offers / My Active — the vendor's OWN sites, from the `[projects]`
 *     @Input (the same `ProjectService.fetchData` emission the dashboard holds).
 *   • Available Work — the staffing-need discovery feed, from
 *     `AvailableWorkService` (`GET /staffing-need`). Fetched lazily on entering
 *     the scope, NOT on dashboard load.
 *
 * Both sources feed the render path via ONE discriminated-union row view-model
 * (`WorkMapRowView`, in the scope util) exposing
 * `position`/`color`/`title`/address, so marker rendering, the row↔marker sync,
 * fit-bounds, the legend and the fallback stay source-agnostic. The `kind`
 * discriminator is consulted only for the card body and the action area.
 *
 * Map plumbing mirrors the internal User Location Map
 * (`user-map-dialog.component.ts`): the `@angular/google-maps` `<google-map>`
 * wrapper for the canvas, imperative `google.maps.Marker`s rendered on
 * `mapInitialized`, and marker-click callbacks bounced back into Angular via
 * NgZone. Markers are grouped by `@googlemaps/markerclusterer` into count
 * bubbles so a dense result stays legible; the clusterer owns each marker's map
 * membership. Markers, listeners, and the clusterer are torn down on scope
 * change and destroy.
 *
 * A marker click opens a shared `<map-info-window>` anchored to that marker,
 * offering a "View project" nav (`/active/:id` or `/invitations`).
 *
 * No-key fallback: `UiSecretsService.getGoogleMapsApiKey()` is the gate. If it
 * yields nothing — or the SDK fails to load — the canvas is suppressed and only
 * the (fully functional) side list renders. Nothing throws.
 */
@Component({
  standalone: true,
  selector: 'app-work-map',
  imports: [CommonModule, GoogleMapsModule, MatTooltipModule],
  templateUrl: './work-map.component.html',
  styleUrl: './work-map.component.scss',
})
export class WorkMapComponent implements OnInit, OnChanges, OnDestroy {
  /** The raw `ProjectService.fetchData` emission (flat `Project { sites }`). */
  @Input() projects: any[] = [];

  // Row `<li>` elements, so a marker click can scroll its paired row into view.
  @ViewChildren('rowEl') rowEls!: QueryList<ElementRef<HTMLElement>>;
  // The one shared InfoWindow — reused for whichever row is selected, never one
  // instance per marker.
  @ViewChild(MapInfoWindow) infoWindowRef?: MapInfoWindow;

  scope: WorkMapScope = 'offers';
  rows: WorkMapRowView[] = [];
  // Cached per-row testids, parallel to `rows` (recomputed in applyScope). See
  // recomputeRowTestIds() for why these are cached fields, not live getters.
  rowTestIds: string[] = [];
  selectedIndex: number | null = null;
  // Which marker is currently painted green — lets a selection change force a
  // repaint on just the previous + new marker rather than all of them.
  private renderedSelectedIndex: number | null = null;
  // Anchors the shared `<map-info-window>`; null when none is open.
  infoWindowPosition: google.maps.LatLngLiteral | null = null;

  // `mapEnabled` gates the side-by-side layout (key present, load not failed);
  // `mapsReady` gates the actual `<google-map>` canvas (SDK finished loading).
  // When `mapEnabled` is false we take the list-only fallback.
  mapEnabled = false;
  mapsReady = false;

  // ── Filter state ────────────────────────────────────────────────────────────
  // Each facet set is computed over the FULL in-scope set (never the post-filter
  // subset), so its counts stay stable as the user narrows. `rows` always holds
  // the post-filter subset that markers AND the side list render.
  //
  // `projectFilter` drives My Offers / My Active, with a per-scope primary axis
  // (My Active → due-date tabs, My Offers → work-type + Critical tabs) plus the
  // shared report-type dropdown. `availableWorkFilter` drives Available Work on
  // its own three axes. The scope toggle resets BOTH so a stale selection never
  // carries across scopes.
  projectFilter: ProjectRowFilter = { reportType: 'all', dueDate: 'all', offers: 'all' };
  dueDateFacets: FacetOption<WorkMapDueDate>[] = [];
  // My Offers filter tabs (All / Quote Requests / Work Assignments / Critical) —
  // single-select; the subsets may overlap (a Critical row is also a Quote
  // Request or Work Assignment), so counts are taken over the full in-scope set.
  offersFacets: FacetOption<OffersFacet>[] = [];
  reportTypeOptions: string[] = [];

  // ── Available Work filter state ────────────────────────────────────────────
  // Three axes: the shared report-type dropdown (seeded from the feed, NOT from
  // a static list), the rush-driven status tabs, and the independent milk-run
  // toggle. There is deliberately NO work-type axis — a need is neither a Quote
  // Request nor a Work Assignment.
  availableWorkFilter: AvailableWorkFilter = {
    reportType: 'all',
    status: 'all',
    milkRunOnly: false,
  };
  availableWorkStatusFacets: FacetOption<AvailableWorkStatusFacet>[] = [];
  // Milk-run is an independent boolean, so it gets its own count rather than a
  // slot in the single-select tab group above.
  availableWorkMilkRunCount = 0;
  // The UNFILTERED in-scope row count. Read only by the genuine-empty state, to
  // tell "nothing in the feed" from "nothing survived the filters" — the second
  // must not be worded as if the vendor's services were the cause.
  availableWorkTotalCount = 0;

  // The full (unfiltered) project-row set for the active project scope — drives
  // the report-type options + due-date counts; `rows` shows its filtered subset.
  private projectRowsAll: WorkMapRow[] = [];

  // The raw Available Work feed as last fetched, and the full (unfiltered) row
  // set derived from it. The needs are cached so a filter change can re-derive
  // rows WITHOUT re-fetching — which now matters more than it used to, since the
  // report-type filter selects the set milk-run proximity is measured over
  // (`buildAvailableWorkRows`). `availableWorkRowsAll` stays the FULL set
  // regardless: the report-type OPTIONS come off it, so narrowing it would strand
  // the vendor on whichever type they had selected.
  private availableWorkNeeds: AvailableWork[] = [];
  private availableWorkRowsAll: AvailableWorkRowView[] = [];
  // The in-flight feed request. Unsubscribed before a new one is issued so two
  // rapid scope toggles can't race — otherwise the LAST TO ARRIVE would win
  // rather than the last issued.
  private availableWorkFetch: Subscription | null = null;

  // ── Available Work loading / error / empty-cause state ────────────────────
  // These four fields exist because an empty `rows` has FOUR different causes on
  // this scope, and each needs a different answer on screen. All are read only
  // by the `@empty` block, in this precedence: loading → error → no enabled
  // services → genuine empty.
  //
  /** True while the feed request is in flight (spinner, not an empty list). */
  availableWorkLoading = false;
  /**
   * The FAILURE envelope's own message when the fetch failed, else null. Shown
   * verbatim (it is server-authored — that is what `ApiFailureError` marks), and
   * the filter controls stay rendered beside it so the vendor can still see and
   * adjust them. Cleared at the top of every fetch so a successful retry never
   * leaves the previous failure on screen.
   */
  availableWorkError: string | null = null;
  /**
   * Whether the vendor holds any ENABLED service — the ONLY thing that tells
   * the two empty feeds apart, since both arrive as an empty array in a SUCCESS
   * envelope. Mirrored client-side from the cached user; see
   * `hasEnabledServiceTypes` for the rule and for which way a stale cache
   * fails. Sampled per fetch rather than once at init (the cached profile is
   * refreshed by `AuthenticationService.me()` on navigation) and held as a
   * field rather than a getter, for the same reason `rowTestIds` is cached: the
   * Google map fires change detection on every pan/zoom/mousemove.
   */
  availableWorkHasEnabledServices = true;
  /**
   * Need ids with an interest write in flight — the in-flight guard for BOTH
   * actions. A set keyed by need id, not one boolean: several cards can be
   * mid-write at once, and each must disable only its own button.
   */
  private readonly interestInFlight = new Set<string>();

  center: google.maps.LatLngLiteral = US_CENTER;
  zoom = US_DEFAULT_ZOOM;
  mapOptions: google.maps.MapOptions = {
    // Map/Satellite (+ terrain) type switcher, matching CONNECT 1.0. Street View
    // and fullscreen stay off — the map is an at-a-glance work overview, not a
    // navigation surface.
    mapTypeControl: true,
    streetViewControl: false,
    fullscreenControl: false,
  };

  private readonly urgencyLegend: { label: string; color: string }[] = [
    URGENCY_META.overdue,
    URGENCY_META.soon,
    URGENCY_META.onTrack,
    URGENCY_META.none,
  ];

  // My Offers legend — rush palette, "Standard" (not "Critical").
  private readonly offersLegend: { label: string; color: string }[] = [
    OFFERS_STATUS_META.needsStaffing,
    OFFERS_STATUS_META.critical,
  ];

  // Available Work legend — the same rush palette, labeled for this surface.
  private readonly availableWorkLegend: { label: string; color: string }[] = [
    AVAILABLE_WORK_STATUS_META.needsStaffing,
    AVAILABLE_WORK_STATUS_META.critical,
  ];

  private googleMap: google.maps.Map | null = null;
  private markers: { index: number; marker: google.maps.Marker }[] = [];
  // Groups dense marker sets into count bubbles. The clusterer OWNS adding/
  // removing each marker from the map, so markers are created without their own
  // `map` and handed to it here.
  private clusterer: MarkerClusterer | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private utility: CommonUtilityServiceService,
    private mapsLoader: GoogleMapsLoaderService,
    private uiSecrets: UiSecretsService,
    private availableWork: AvailableWorkService,
    private zone: NgZone,
    private router: Router,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.initMaps();
    this.applyScope();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Rebuild rows whenever the fed project set changes (the dashboard re-emits
    // on refresh) — but ONLY on the two scopes `[projects]` actually drives.
    //
    // Available Work is now fully independent of it: milk-run distances are
    // measured need-to-need, so nothing on that scope reads `[projects]` any
    // more. Rebuilding there would be pure cost with a real downside — it clears
    // `selectedIndex` and closes the InfoWindow, so a background refresh would
    // silently deselect the pin the vendor had open. My Offers / My Active still
    // rebuild here, and `setScope` rebuilds from the CURRENT `this.projects` on
    // the way back in, so skipping the rebuild can't leave them stale.
    // Markers re-render only once a map exists.
    if (changes['projects'] && this.scope !== 'availableWork') {
      // Rows are rebuilt here, so a retained selection would point at a
      // possibly-reordered row (wrong marker highlight / stale InfoWindow
      // anchor) — clear it. The active filter is intentionally left untouched:
      // a background data refresh shouldn't reset what the vendor is filtering.
      this.selectedIndex = null;
      this.closeInfoWindow();
      this.applyScope();
    }
  }

  /** The row backing the currently-open InfoWindow, or null when none is open. */
  get selectedRow(): WorkMapRowView | null {
    return this.selectedIndex !== null
      ? this.rows[this.selectedIndex] ?? null
      : null;
  }

  /**
   * Scope-specific legend. My Active uses the 4-bucket urgency legend; My Offers
   * and Available Work both use the rush palette, with their own labels
   * (Standard vs Needs staffing).
   */
  get legend(): { label: string; color: string }[] {
    if (this.scope === 'active') {
      return this.urgencyLegend;
    }
    return this.scope === 'availableWork' ? this.availableWorkLegend : this.offersLegend;
  }

  /**
   * The report-type dropdown's current value. The dropdown is shared by all
   * three scopes but each keeps its own filter model, so the template reads the
   * active scope's value through here rather than binding one of them directly.
   */
  get activeReportType(): string {
    return this.scope === 'availableWork'
      ? this.availableWorkFilter.reportType
      : this.projectFilter.reportType;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearMarkers();
    this.closeInfoWindow();
    this.googleMap = null;
  }

  // ── Scope toggle ────────────────────────────────────────────────────────────

  setScope(scope: WorkMapScope): void {
    if (this.scope === scope) {
      return;
    }
    this.scope = scope;
    this.selectedIndex = null;
    this.closeInfoWindow();
    // Reset BOTH filter models so a selection made in one scope (a My Offers
    // work-type/Critical tab, a My Active due-date tab, an Available Work status
    // tab or milk-run toggle) never carries into another.
    this.projectFilter = { reportType: 'all', dueDate: 'all', offers: 'all' };
    this.availableWorkFilter = { reportType: 'all', status: 'all', milkRunOnly: false };
    if (scope === 'availableWork') {
      // Fetched lazily, on entering the scope — the dashboard shouldn't pay for
      // a discovery-feed request the vendor may never open.
      this.loadAvailableWork();
    } else {
      this.availableWorkFetch?.unsubscribe();
      this.availableWorkFetch = null;
      // Cancelling the request also has to retire its spinner — otherwise
      // re-entering the scope would paint a stale "loading" for the instant
      // before the new fetch sets it again.
      this.availableWorkLoading = false;
    }
    this.applyScope();
  }

  /** Rebuild the row set for the current scope and (re-)render markers. */
  private applyScope(): void {
    this.rows =
      this.scope === 'availableWork'
        ? this.buildAvailableWorkRows()
        : this.buildProjectRows();
    this.recomputeRowTestIds();
    this.renderMarkers();
  }

  private buildProjectRows(): ProjectRowView[] {
    // Full in-scope set → facets (options + counts) over it, then the filtered
    // subset feeds both the list and the markers.
    this.projectRowsAll = buildWorkMapRows(this.projects, this.scope);
    this.recomputeProjectFacets();
    return filterProjectRows(this.projectRowsAll, this.projectFilter, this.scope).map(
      (row) => {
        const address = this.utility.formatAddress({ site: row.site });
        // My Active is due-date driven (urgency palette); My Offers is an
        // opportunity scope (rush-based palette — Critical red / Standard grey).
        const color =
          this.scope === 'active'
            ? URGENCY_META[row.urgency].color
            : OFFERS_STATUS_META[workMapRowStatus(row)].color;
        const workType = workMapRowWorkType(row);
        return {
          kind: 'project' as const,
          key: row.key,
          position: row.position,
          color,
          title: row.siteNumber != null ? `#${row.siteNumber}` : 'Site',
          streetAddress: address.streetAddress,
          cityStateZip: address.cityStateZip,
          row,
          workType,
          workTypeLabel: workType === 'workAssignment' ? 'Work Assignment' : 'Quote Request',
        };
      }
    );
  }

  /**
   * Recompute the project scopes' report-type options + BOTH primary-axis facet
   * sets (My Active's due-date counts + My Offers' work-type/Critical counts)
   * over the full in-scope set. Both are always rebuilt so whichever tab group
   * the active scope renders shows live counts (or 0s) the instant it's shown.
   */
  private recomputeProjectFacets(): void {
    this.reportTypeOptions = projectReportTypeOptions(this.projectRowsAll);
    const counts = projectDueDateCounts(this.projectRowsAll);
    this.dueDateFacets = [
      { value: 'all', label: 'All', count: counts.all },
      { value: 'upcoming', label: 'Upcoming', count: counts.upcoming },
      { value: 'pastDue', label: 'Past Due', count: counts.pastDue },
    ];
    const offersCounts = offersFacetCounts(this.projectRowsAll);
    this.offersFacets = [
      { value: 'all', label: 'All', count: offersCounts.all },
      { value: 'quoteRequest', label: 'Quote Requests', count: offersCounts.quoteRequest },
      { value: 'workAssignment', label: 'Work Assignments', count: offersCounts.workAssignment },
      { value: 'critical', label: 'Critical', count: offersCounts.critical },
    ];
  }

  // ── Available Work data path ──────────────────────────────────────────────

  /**
   * Fetch the Available Work feed. Any in-flight request is cancelled first, so
   * rapid scope toggles can't deliver out of order. The service THROWS on a
   * FAILURE envelope (it never emits one as an empty list), which is what keeps
   * "nothing available" distinguishable from "the fetch failed": the two land in
   * different `@empty` states rather than reading as the same blank list. Either
   * way the row set ends up empty with its filter controls intact.
   *
   * `availableWorkHasEnabledServices` is sampled HERE, not at init, because it
   * is the only thing separating the two empty feeds and the cached profile it
   * reads is refreshed on navigation.
   */
  private loadAvailableWork(): void {
    this.availableWorkFetch?.unsubscribe();
    this.availableWorkNeeds = [];
    this.availableWorkLoading = true;
    // Cleared per fetch so a successful retry doesn't render the previous
    // failure's message underneath a fresh (or newly empty) result.
    this.availableWorkError = null;
    this.availableWorkHasEnabledServices = hasEnabledServiceTypes(getStoredUser());
    this.availableWorkFetch = this.availableWork
      .fetch()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (needs) => {
          this.availableWorkLoading = false;
          this.availableWorkNeeds = needs ?? [];
          this.applyScope();
        },
        error: (err: unknown) => {
          this.availableWorkLoading = false;
          // The FAILURE envelope's own message when there is one (that is what
          // `ApiFailureError` marks as server-authored and safe to show);
          // otherwise generic copy, because a transport-level error's `.message`
          // is technical, not operator-facing.
          this.availableWorkError = serverErrorMessage(
            err,
            "We couldn't load available work. Please try again."
          );
          this.availableWorkNeeds = [];
          this.applyScope();
        },
      });
  }

  // ── Available Work actions (express / withdraw interest) ──────────────────

  /** True while this need has an interest write in flight — the in-flight guard. */
  isInterestPending(view: AvailableWorkRowView): boolean {
    return this.interestInFlight.has(view.need.id);
  }

  /**
   * Express Interest → `POST /staffing-need/{id}/interest`.
   *
   * Two properties this method exists to hold:
   *  - **The card commits only after the call resolves.** Nothing is written to
   *    `need` before `next` fires, so a failed express can never leave the card
   *    claiming an interest the server refused.
   *  - **The click does not also select the row.** The card `<li>` carries
   *    `(click)="onRowClick(i)"`, which would zoom the map and open the
   *    InfoWindow behind the action — so the event stops here.
   */
  expressInterest(view: AvailableWorkRowView, event: Event): void {
    event.stopPropagation();
    if (this.interestInFlight.has(view.need.id) || view.need.expressedInterest) {
      return;
    }
    this.interestInFlight.add(view.need.id);
    this.availableWork
      .expressInterest(view.need.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.interestInFlight.delete(view.need.id);
          this.commitInterest(view, true);
        },
        error: (err: unknown) => {
          this.interestInFlight.delete(view.need.id);
          // Card state is left exactly as it was. The server's FAILURE copy is
          // worth showing here — "This work has already been staffed." /
          // "This work is no longer available." tell the vendor something the
          // card cannot.
          this.toastr.error(
            serverErrorMessage(err, "We couldn't register your interest. Please try again.")
          );
        },
      });
  }

  /**
   * Withdraw → `DELETE /staffing-need/{id}/interest`. Same guard and same
   * commit-after-resolve discipline as {@link expressInterest}.
   *
   * <b>No success toast.</b> connect-service answers "Your interest has been
   * withdrawn." on every branch of this endpoint — including when there was no
   * interest to withdraw, and when the need is closed or unknown (uniform by
   * design, so no answer is an existence oracle). Echoing it would assert
   * something about what happened that the message cannot support.
   *
   * <b>Which is why the card commits only on `removed: true`.</b> That flag is
   * the one thing the uniform answer discriminates on: whether this vendor's
   * own interest was actually pulled. On `false` the withdrawal did NOT happen
   * — the need went `STAFFED` under them on the hourly push, the interest was
   * already gone, the id is unknown, or it changed concurrently — and nothing
   * was forwarded to Salesforce either, so flipping the card back to Express
   * Interest would tell them a withdrawal succeeded that did not.
   *
   * On `false` we re-fetch instead of committing. The four causes are
   * indistinguishable by design, and a re-fetch is the one response that is
   * right for all of them: a `STAFFED` need is never served, so it drops out of
   * the feed entirely, and an interest that was already absent comes back as
   * `expressedInterest: false`. It costs the vendor their selection (the
   * re-fetch rebuilds, so the InfoWindow closes) — acceptable on a path this
   * rare, unlike a card that lies. The toast is what stops the feed changing
   * under them from reading as a glitch; it says only what the client can
   * actually know, which is that the withdrawal is unconfirmed.
   */
  withdrawInterest(view: AvailableWorkRowView, event: Event): void {
    event.stopPropagation();
    if (this.interestInFlight.has(view.need.id) || !view.need.expressedInterest) {
      return;
    }
    this.interestInFlight.add(view.need.id);
    this.availableWork
      .withdrawInterest(view.need.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (removed) => {
          this.interestInFlight.delete(view.need.id);
          if (!removed) {
            this.toastr.info(WITHDRAWAL_UNCONFIRMED_MESSAGE);
            this.loadAvailableWork();
            return;
          }
          this.commitInterest(view, false);
        },
        error: (err: unknown) => {
          this.interestInFlight.delete(view.need.id);
          this.toastr.error(
            serverErrorMessage(err, "We couldn't withdraw your interest. Please try again.")
          );
        },
      });
  }

  /**
   * Flip one card's interest state, in place on the feed row.
   *
   * Deliberately NOT an `applyScope()` / `refilter()` rebuild. Two reasons:
   *  - `view.need` is the same object held in `availableWorkNeeds`, so the
   *    change survives a later rebuild (a `[projects]` re-emit) for free, and no
   *    derived field on the row view depends on it — `status`, `reportType`,
   *    `metadataLine`, `position` and `isMilkRun` are all independent of
   *    interest.
   *  - A rebuild clears `selectedIndex` and closes the InfoWindow, so acting on
   *    a card would silently deselect the pin the vendor had just opened.
   *
   * `expressedOn` is stamped locally so the card can say WHEN without a
   * re-fetch; the next fetch replaces it with the server's own instant, which
   * is authoritative.
   */
  private commitInterest(view: AvailableWorkRowView, expressed: boolean): void {
    view.need.expressedInterest = expressed;
    view.need.expressedOn = expressed ? new Date().toISOString() : null;
  }

  /**
   * "Manage Services" from the no-enabled-services empty state — the one empty
   * case with somewhere to go. `/services-zones` is where a vendor's service
   * statuses live, so it is also where they can see the real ones if the cached
   * profile this state was chosen from happened to be stale.
   */
  goToServices(): void {
    this.router.navigate(['/services-zones']);
  }

  /**
   * Map the cached feed into row views, order them by proximity, recompute the
   * facets over that full set, and return the filtered subset the list + markers
   * render. Field-level rules all live in the (pure) scope util; this composes
   * them with the two things that need Angular — the shared address formatter
   * and the status palette.
   *
   * A need with no `geoPoint` gets `position: null`: it renders in the list and
   * counts in every facet, but plots no pin (see `renderMarkers`).
   */
  private buildAvailableWorkRows(): AvailableWorkRowView[] {
    const bases = this.availableWorkNeeds.map((need) => {
      const address = this.utility.formatAddress(availableWorkAddressSource(need));
      const status = workMapRowStatus(need);
      return {
        kind: 'availableWork' as const,
        key: need.id,
        position: availableWorkPosition(need),
        color: AVAILABLE_WORK_STATUS_META[status].color,
        title: need.propertyName?.trim() || 'Property',
        streetAddress: address.streetAddress,
        cityStateZip: address.cityStateZip,
        need,
        status,
        statusLabel: AVAILABLE_WORK_STATUS_META[status].label,
        reportType: availableWorkReportType(need),
        metadataLine: availableWorkMetadataLine(need),
        // Property identity, so a dual-scope property's two needs are not each
        // other's milk run. Carried on the row (not re-derived per comparison)
        // because the proximity loop is O(n²) over the reference set.
        propertyKey: availableWorkPropertyKey(need),
      };
    });

    // ── The milk-run reference set ────────────────────────────────────────────
    // Each need's nearest neighbour is ANOTHER need (self excluded by id), taken
    // from the set narrowed by the REPORT-TYPE filter only — matching CONNECT
    // 1.5, which applies its report-type filter before proximity "so milk-run
    // reflects what's actually shown". Two reasons it stops there:
    //   * NOT the status tabs. Badges would flicker as the vendor toggles Needs
    //     Staffing / Critical, changing a fact about the geography that has
    //     nothing to do with which status they are looking at.
    //   * NOT the Milk Run Only toggle — that would be CIRCULAR. That filter
    //     selects on the very flag this computes. So: compute the flag over a
    //     set that ignores it, THEN let the filter apply.
    // This is why the computation lives here at filter time rather than at
    // row-assembly time: it now depends on the active report type.
    const referenceSet = bases.filter((base) =>
      matchesAvailableWorkReportType(base, this.availableWorkFilter)
    );
    // A need outside the reference set is not measured at all (measuring it
    // against a set it isn't in would be incoherent) — it carries NaN, i.e. "not
    // near", and is filtered out of the list anyway.
    const inReference = new Set(referenceSet.map((base) => base.key));
    const views = bases.map((base) => ({
      ...base,
      ...(inReference.has(base.key)
        ? availableWorkProximity(base, referenceSet)
        : unknownAvailableWorkProximity()),
    }));

    this.availableWorkRowsAll = sortAvailableWorkRowsByProximity(views);
    this.recomputeAvailableWorkFacets();
    return filterAvailableWorkRows(this.availableWorkRowsAll, this.availableWorkFilter);
  }

  /**
   * Recompute the Available Work report-type options + status/milk-run counts
   * over the full in-scope set. Always runs — including on an empty AND on a
   * failed fetch, where it seeds every option with a 0 count and empties the
   * report-type list rather than letting the controls vanish (or letting a
   * previous fetch's options linger behind zero counts).
   */
  private recomputeAvailableWorkFacets(): void {
    this.reportTypeOptions = availableWorkReportTypeOptions(this.availableWorkRowsAll);
    const counts = availableWorkFacetCounts(this.availableWorkRowsAll);
    this.availableWorkStatusFacets = [
      { value: 'all', label: 'All', count: counts.all },
      { value: 'needsStaffing', label: 'Needs Staffing', count: counts.needsStaffing },
      { value: 'critical', label: 'Critical', count: counts.critical },
    ];
    this.availableWorkMilkRunCount = counts.milkRun;
    this.availableWorkTotalCount = counts.all;
  }

  // ── Filters ───────────────────────────────────────────────────────────────
  // The report-type dropdown is shared by all three scopes (its options are
  // seeded from whichever set is in scope). The tab rows are per-scope: due-date
  // for My Active, work-type/Critical for My Offers, and status + the
  // independent milk-run toggle for Available Work.

  onReportTypeFilterChange(value: string): void {
    const reportType = value || 'all';
    if (this.scope === 'availableWork') {
      this.availableWorkFilter = { ...this.availableWorkFilter, reportType };
    } else {
      this.projectFilter = { ...this.projectFilter, reportType };
    }
    this.refilter();
  }

  /** Status tab (All / Needs Staffing / Critical) — Available Work. */
  setAvailableWorkStatusFilter(value: AvailableWorkStatusFacet): void {
    if (this.availableWorkFilter.status === value) {
      return;
    }
    this.availableWorkFilter = { ...this.availableWorkFilter, status: value };
    this.refilter();
  }

  /**
   * Milk-run toggle — Available Work. An independent boolean, not a member of
   * the status tab group, so it composes with the other two axes.
   */
  toggleMilkRunOnly(): void {
    this.availableWorkFilter = {
      ...this.availableWorkFilter,
      milkRunOnly: !this.availableWorkFilter.milkRunOnly,
    };
    this.refilter();
  }

  setDueDateFilter(value: WorkMapDueDate): void {
    if (this.projectFilter.dueDate === value) {
      return;
    }
    this.projectFilter = { ...this.projectFilter, dueDate: value };
    this.refilter();
  }

  /** Work-type/Critical tab (All / Quote Requests / Work Assignments / Critical) — My Offers. */
  setOffersFilter(value: OffersFacet): void {
    if (this.projectFilter.offers === value) {
      return;
    }
    this.projectFilter = { ...this.projectFilter, offers: value };
    this.refilter();
  }

  /** Re-run the active scope's filter → rebuild rows/markers, resetting any open selection. */
  private refilter(): void {
    this.selectedIndex = null;
    this.closeInfoWindow();
    this.applyScope();
  }

  // ── Maps load / no-key fallback ───────────────────────────────────────────────

  private initMaps(): void {
    const apiKey = this.uiSecrets.getGoogleMapsApiKey();
    if (!apiKey) {
      // No key available — list-only fallback. No canvas, no error.
      this.mapEnabled = false;
      this.mapsReady = false;
      return;
    }
    this.mapEnabled = true;
    if (this.mapsLoader.isLoaded()) {
      this.mapsReady = true;
      return;
    }
    this.mapsLoader
      .load(apiKey)
      .then(() => this.zone.run(() => (this.mapsReady = true)))
      .catch(() =>
        // SDK failed to load — degrade to the same list-only fallback.
        this.zone.run(() => {
          this.mapEnabled = false;
          this.mapsReady = false;
        })
      );
  }

  /** Fired once `<google-map>` has constructed its underlying `google.maps.Map`. */
  onMapInitialized(map: google.maps.Map): void {
    this.googleMap = map;
    this.renderMarkers();
  }

  // ── Markers ───────────────────────────────────────────────────────────────────

  private renderMarkers(): void {
    if (!this.googleMap) {
      return;
    }
    this.clearMarkers();
    this.rows.forEach((view, index) => {
      const position = view.position;
      if (!position) {
        return;
      }
      // No `map:` here — the clusterer (below) owns adding each marker to the
      // map so it can group/hide them inside cluster bubbles. Setting `map`
      // too would double-add and desync the clusterer.
      const marker = new google.maps.Marker({
        position,
        icon: this.markerIcon(view.color),
        title: this.markerTitle(view),
      });
      marker.addListener('click', () => this.onMarkerClick(index));
      this.markers.push({ index, marker });
    });
    this.clusterer = this.createClusterer(
      this.googleMap,
      this.markers.map((entry) => entry.marker),
    );
    this.fitBounds();
    this.applyMarkerColors();
  }

  /**
   * Thin factory wrapper around `new MarkerClusterer(...)` — its constructor
   * reaches into `google.maps.OverlayView`'s prototype (panes/projection), which
   * only a real Maps JS runtime provides, so the component spec stubs this one
   * method (swapping a fake clusterer) rather than re-implementing OverlayView
   * in jsdom. Mirrors user-map-dialog's construction seam.
   */
  protected createClusterer(
    map: google.maps.Map,
    markers: google.maps.Marker[],
  ): MarkerClusterer {
    return new MarkerClusterer({
      map,
      markers,
      // Default onClusterClick zooms/fits the map to the cluster's bounds.
      renderer: this.clusterRenderer,
    });
  }

  /**
   * Cluster bubbles colored by DENSITY (light blue → navy), not the default
   * blue→red (red would collide with the overdue/critical status colors). Scales
   * the fill from CLUSTER_LOW_COLOR to CLUSTER_HIGH_COLOR by count relative to
   * the largest cluster (sqrt to spread mid-range), sizes the circle by count,
   * and picks a contrasting label color. Mirrors the User Location Map.
   */
  private readonly clusterRenderer: Renderer = {
    render: (cluster, stats) => {
      const count = cluster.count;
      const max = stats.clusters.markers.max || count;
      const t = max > 1 ? Math.min(1, Math.sqrt(count / max)) : 0;
      const fill = lerpColor(CLUSTER_LOW_COLOR, CLUSTER_HIGH_COLOR, t);
      return new google.maps.Marker({
        position: cluster.position,
        zIndex: 1000 + count,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: fill,
          fillOpacity: 0.92,
          strokeColor: '#ffffff',
          strokeWeight: 2,
          scale: 14 + Math.sqrt(count) * 2.5,
        },
        label: {
          text: String(count),
          color: labelColorFor(fill),
          fontSize: '12px',
          fontWeight: '600',
        },
      });
    },
  };

  /**
   * Re-tint every marker: the selected row's marker gets the brand-green
   * highlight, all others keep their status color. Called after (re)render and
   * on every selection change so exactly one marker is ever green.
   */
  private applyMarkerColors(): void {
    this.markers.forEach((entry) => {
      const base = this.rows[entry.index]?.color ?? '#6b7280';
      const color =
        entry.index === this.selectedIndex ? SELECTED_MARKER_COLOR : base;
      entry.marker.setIcon(this.markerIcon(color));
    });
    // setIcon updates the marker's icon data, but these markers don't always
    // repaint on a bare setIcon — they only redraw on a map zoom/pan. Under
    // clustering the clusterer OWNS each marker's map (a selected marker may
    // even be hidden inside a cluster), so the old setMap(null)/setMap(map)
    // toggle would fight the clusterer and desync it. Ask the clusterer to
    // re-render instead — it repaints the individual (un-clustered) markers,
    // including the recolored selected one. Only when there is no clusterer
    // (e.g. the no-key path, or tests without one) do we fall back to the
    // per-marker setMap toggle, and only for the markers whose selected state
    // changed so we don't churn every marker on each click.
    if (this.clusterer) {
      this.clusterer.render();
    } else {
      [this.selectedIndex, this.renderedSelectedIndex].forEach((idx) => {
        if (idx === null) {
          return;
        }
        const entry = this.markers.find((m) => m.index === idx);
        if (entry) {
          this.repaintMarker(entry.marker);
        }
      });
    }
    this.renderedSelectedIndex = this.selectedIndex;
  }

  /** Force a marker to redraw its (already-updated) icon — no-clusterer fallback only. */
  private repaintMarker(marker: google.maps.Marker): void {
    const map = marker.getMap();
    if (!map) {
      return;
    }
    marker.setMap(null);
    marker.setMap(map as google.maps.Map);
  }

  private clearMarkers(): void {
    // Detach the clusterer entirely (its OverlayView + map `idle` listener), not
    // just its markers — clearMarkers() alone would leave both attached and leak
    // one per re-render. setMap(null) tears both down. Runs on every re-render
    // and on ngOnDestroy.
    this.clusterer?.setMap(null);
    this.clusterer = null;
    this.markers.forEach((entry) => entry.marker.setMap(null));
    this.markers = [];
    this.renderedSelectedIndex = null;
  }

  private markerIcon(color: string): google.maps.Symbol {
    return {
      path: google.maps.SymbolPath.CIRCLE,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 1.5,
      scale: 8,
    };
  }

  private markerTitle(view: WorkMapRowView): string {
    return [view.title, view.streetAddress || view.cityStateZip]
      .filter(Boolean)
      .join(' · ');
  }

  private fitBounds(): void {
    if (!this.googleMap) {
      return;
    }
    const positions = this.markers
      .map((entry) => this.rows[entry.index]?.position)
      .filter((p): p is google.maps.LatLngLiteral => !!p);
    if (positions.length === 0) {
      this.googleMap.setCenter(US_CENTER);
      this.googleMap.setZoom(US_DEFAULT_ZOOM);
      return;
    }
    if (positions.length === 1) {
      this.googleMap.setCenter(positions[0]);
      this.googleMap.setZoom(FOCUS_ZOOM);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    positions.forEach((p) => bounds.extend(p));
    this.googleMap.fitBounds(bounds);
  }

  // ── Bi-directional row ↔ marker sync ──────────────────────────────────────────

  /** Row click → highlight it and focus/zoom its marker on the map. */
  onRowClick(index: number): void {
    this.selectedIndex = index;
    this.infoWindowPosition = this.rows[index]?.position ?? null;
    this.focusMarker(index);
    this.applyMarkerColors();
    // Clicking the list row selects the pin exactly like clicking the pin does:
    // open its InfoWindow too. Deferred so the `*`-gated <map-info-window> is in
    // the view before we open it (same sequencing as onMarkerClick).
    setTimeout(() => this.infoWindowRef?.open());
  }

  private focusMarker(index: number): void {
    const position = this.rows[index]?.position;
    if (!position || !this.googleMap) {
      return;
    }
    this.googleMap.setCenter(position);
    this.googleMap.setZoom(FOCUS_ZOOM);
  }

  /**
   * Marker click → highlight the paired row, scroll it into view, and open the
   * shared InfoWindow anchored to that marker.
   */
  private onMarkerClick(index: number): void {
    // Google Maps click listeners fire OUTSIDE Angular's zone — bounce back in
    // so the selection binding, scroll, and InfoWindow position run under
    // change detection.
    this.zone.run(() => {
      this.selectedIndex = index;
      this.infoWindowPosition = this.rows[index]?.position ?? null;
      this.scrollRowIntoView(index);
      this.applyMarkerColors();
    });
    // zone.run() above triggers a synchronous CD tick on exit, which renders
    // the `*`-gated `<map-info-window>` and populates the ViewChild BEFORE
    // this setTimeout macrotask runs — same sequencing user-map-dialog's
    // openInfoWindow() relies on.
    setTimeout(() => this.infoWindowRef?.open());
  }

  private scrollRowIntoView(index: number): void {
    // Defer to the next tick so the `selected` class is applied first, then
    // bring the row into view WITHIN the list's own scroll box only — never the
    // page. On the stacked (≤md) layout the list has no max-height / internal
    // scroll, so a page-level `el.scrollIntoView()` would yank the viewport down
    // to the list and hide the map + the InfoWindow the pin just opened (the
    // whole point of the click). Adjusting the list's own scrollTop keeps the
    // page put; it's simply a no-op when the list can't scroll (narrow layout).
    setTimeout(() => {
      const el = this.rowEls?.get(index)?.nativeElement as
        | HTMLElement
        | undefined;
      const list = el?.closest('.work-map__list') as HTMLElement | null;
      if (!el || !list) {
        return;
      }
      const listRect = list.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      if (elRect.top < listRect.top) {
        list.scrollTop -= listRect.top - elRect.top;
      } else if (elRect.bottom > listRect.bottom) {
        list.scrollTop += elRect.bottom - listRect.bottom;
      }
    });
  }

  // ── Marker InfoWindow ─────────────────────────────────────────────────────────

  /** "View Project →" click inside the InfoWindow — navigates per the active scope. */
  viewProject(): void {
    const row = this.selectedRow;
    // An Available Work need has no project, no proposal and no workspace, so
    // there is nothing to navigate to — bail rather than dereference a `row`
    // that only project-backed views carry. Belt and braces now: the template
    // no longer renders this button for a need row, and `workMapProjectRoute`
    // returns no route for the scope, so this guard is unreachable from the UI.
    // It stays as the last line of defence for any future caller.
    if (!row || row.kind !== 'project') {
      return;
    }
    const route = workMapProjectRoute(row.row, this.scope);
    this.closeInfoWindow();
    if (route) {
      // Carry the project id so a LIST destination (My Offers → /invitations) can
      // scroll to + highlight that project after it loads. Harmless for the
      // /active workspace deep-link, which ignores it.
      //
      // Stamp the back origin as 'dashboard' so the workspace's "Back to Active"
      // navigates to the Active list explicitly. The workspace otherwise does
      // location.back(), which here would pop to the Dashboard (where the pin
      // was clicked) — contradicting the button's label. Inert for the
      // /invitations list destination, which doesn't read it.
      this.router.navigate(route, {
        state: {
          scrollToProjectId: row.row.projectId,
          workspaceBackType: 'dashboard',
        },
      });
    }
  }

  closeInfoWindow(): void {
    this.infoWindowPosition = null;
  }

  // ── Template helpers ──────────────────────────────────────────────────────────

  trackByRow(_index: number, view: WorkMapRowView): string {
    return view.key;
  }

  // Per-row testid identity (Spec 13, Task 3 — stability pass): the row `<li>`
  // and its inner work-type control were previously keyed on `$index`, which
  // shifts on every filter/scope change or refresh. `view.key` is already the
  // row view-model's own "stable, list-unique row key" (see RowViewBase above /
  // trackByRow) — reuse it here instead of introducing a second identity source.
  // `createTestIdScope` dedupes the (currently impossible, but defensive) case
  // of two rows sharing a key.
  //
  // Cached (recomputed once per `rows` rebuild in applyScope), NOT live getters:
  // the template reads these by index inside the `@for` over `rows`, so a
  // getter that rebuilt the whole array on each access would be O(N²) per
  // change-detection — and the Google map fires CD on every pan/zoom/mousemove.
  private recomputeRowTestIds(): void {
    const rowScope = createTestIdScope();
    this.rowTestIds = this.rows.map((view) =>
      buildTestId(rowScope, 'dashboard-map-row', view.key)
    );
  }

  /** Due-date urgency label for a My Active project row. */
  rowDueLabel(view: ProjectRowView): string {
    return URGENCY_META[view.row.urgency].label;
  }

  /** Rush-based status label for a My Offers project row ("Standard" / "Critical"). */
  rowStatusLabel(view: ProjectRowView): string {
    return OFFERS_STATUS_META[workMapRowStatus(view.row)].label;
  }
}
